/**
 * Shared preview URL helpers.
 *
 * The `t` query param holds a base64url-encoded JSON payload. This hides plain
 * query keys from casual editing; it is not encryption — anyone can decode it.
 * For tamper-proof or confidential links, issue short-lived signed tokens from your API.
 */

export const SHARED_PREVIEW_TOKEN_PARAM = 't';

const PAYLOAD_VERSION = 1 as const;

type EncodedPayloadV1 = {
   v: typeof PAYLOAD_VERSION;
   f: string;
   ct?: string;
   th?: string;
   sl?: string;
   /** Allow Tools panel (recipient can edit coat / thickness / zoom) */
   ae: boolean;
};

export type SharedPreviewParsed = {
   folderId: string;
   coatType: string;
   thickness: string;
   layer: string;
   allowEdit: boolean;
};

export type ParseSharedPreviewResult =
   | { ok: true; data: SharedPreviewParsed }
   | { ok: false; reason: 'invalid_token' | 'missing_folder' };

function toBase64Url(json: string): string {
   const bytes = new TextEncoder().encode(json);
   let binary = '';
   bytes.forEach((b) => {
      binary += String.fromCharCode(b);
   });
   return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(token: string): string {
   const b64 = token.replace(/-/g, '+').replace(/_/g, '/');
   const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
   const bin = atob(b64 + pad);
   const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
   return new TextDecoder().decode(bytes);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
   return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function normalizeDecodedPayload(raw: unknown): SharedPreviewParsed | null {
   if (!isPlainObject(raw)) return null;
   if (raw.v !== PAYLOAD_VERSION) return null;
   if (typeof raw.f !== 'string' || !raw.f.trim()) return null;

   const coatType = typeof raw.ct === 'string' ? raw.ct : '';
   const thickness = typeof raw.th === 'string' ? raw.th : '';
   const layer = typeof raw.sl === 'string' ? raw.sl : '';
   const allowEdit = raw.ae !== false;

   return {
      folderId: raw.f.trim(),
      coatType,
      thickness,
      layer,
      allowEdit,
   };
}

/** Build token for `/preview/shared?t=…` */
export function encodeSharedPreviewToken(input: {
   folderId: string;
   coatType?: string;
   thickness?: string;
   layer?: string;
   allowEdit: boolean;
}): string {
   const body: EncodedPayloadV1 = {
      v: PAYLOAD_VERSION,
      f: input.folderId,
      ae: input.allowEdit,
   };
   if (input.coatType) body.ct = input.coatType;
   if (input.thickness) body.th = input.thickness;
   if (input.layer) body.sl = input.layer;
   return toBase64Url(JSON.stringify(body));
}

export function tryDecodeSharedPreviewToken(token: string): SharedPreviewParsed | null {
   try {
      const json = fromBase64Url(token);
      return normalizeDecodedPayload(JSON.parse(json) as unknown);
   } catch {
      return null;
   }
}

/** Legacy plain query: `edit=0` etc. hides Tools when no token is used. */
export function legacySharedPreviewAllowsEdit(searchParams: URLSearchParams): boolean {
   const raw = searchParams.get('edit');
   if (raw === null || raw === '') return true;
   return !['0', 'false', 'no', 'off'].includes(raw.trim().toLowerCase());
}

export function parseSharedPreviewQuery(searchParams: URLSearchParams): ParseSharedPreviewResult {
   const t = searchParams.get(SHARED_PREVIEW_TOKEN_PARAM);
   if (t != null && t.trim() !== '') {
      const decoded = tryDecodeSharedPreviewToken(t.trim());
      if (!decoded) return { ok: false, reason: 'invalid_token' };
      return { ok: true, data: decoded };
   }

   const f = searchParams.get('f')?.trim() || '';
   if (!f) return { ok: false, reason: 'missing_folder' };

   return {
      ok: true,
      data: {
         folderId: f,
         coatType: searchParams.get('ct') || '',
         thickness: searchParams.get('th') || '',
         layer: searchParams.get('sl') || '',
         allowEdit: legacySharedPreviewAllowsEdit(searchParams),
      },
   };
}
